import { doorRules } from '@planner/config';
import type { Order, OrderItem } from '@planner/shared';
import {
  doorCartTotals,
  evaluateLockFit,
  isDrawingApprovalValid,
  matchStoredDoorLines,
  parseDoorsDesign,
  storedDoorSize,
  validateDoorLineDetails,
  type DoorConfig,
  type ManufacturedSize,
} from '@planner/shared/doors';
import { region } from './env.js';

export interface CartLine {
  door: DoorConfig;
  item: OrderItem;
  size: ManufacturedSize;
  errors: string[];
}

export type CartView =
  { valid: false; message: string } | { valid: true; lines: CartLine[]; totalsValid: boolean };

export function inspectCart(order: Order): CartView {
  const design = parseDoorsDesign(order.design);
  const lineIds = design && matchStoredDoorLines(design, order.items, order.doorLineIds);
  if (
    !design ||
    design.doors.length === 0 ||
    design.doors.length > doorRules.maxDoorsPerOrder ||
    !lineIds
  ) {
    return {
      valid: false,
      message: 'The saved Cart could not be matched to its doors.',
    };
  }
  const lines: CartLine[] = [];
  for (const door of design.doors) {
    const item = order.items[lineIds.indexOf(door.lineId)];
    const size = item && storedDoorSize(item);
    if (!item || !size)
      return { valid: false, message: 'A saved door could not be matched to its item.' };
    const details = validateDoorLineDetails(door);
    const errors: string[] = [];
    if (!details.valid) errors.push(details.message);
    if (evaluateLockFit(door.lockHeight, door.doorType, size.height).status !== 'valid')
      errors.push('Handle position is incompatible with the manufactured door height.');
    if (!Number.isFinite(item.unitPrice) || item.unitPrice <= 0)
      errors.push('This door has no valid saved price.');
    if (!isDrawingApprovalValid(door, size))
      errors.push('Review and approve this drawing before exporting.');
    lines.push({ door, item, size, errors });
  }
  const total = doorCartTotals(order.items, region);
  return {
    valid: true,
    lines,
    totalsValid:
      order.totals.subtotal === total.subtotal &&
      order.totals.tax === total.tax &&
      order.totals.total === total.total &&
      order.totals.currency === total.currency,
  };
}
